/**
 * Generic number lines: budget balances, indicators and keyboard-wedge output (`ST,GS,+   12.345g`,
 * `US,NT,-    0.002 g`, `    12.345 g`, `12.345`). Stability comes from an ST/US header or an Ohaus-style
 * `?` marker when present; otherwise the software window decides.
 */
import type { BalanceEvent, BalanceProtocol } from "../types";
import { toGrams } from "./units";

const NUMBER = /([+-])?\s*(\d+(?:[.,]\d+)?)\s*(mg|kg|g|ct|oz|lb)?\b/i;

export function parseGenericLine(line: string, at: number): BalanceEvent | null {
  const trimmed = line.trim();
  const match = NUMBER.exec(trimmed);
  if (!match) return null;
  const [, sign = "", digits = "", unit] = match;
  const overload = /^(OL|OVER|\+OL)/i.test(trimmed);
  const stable = /^ST\b/i.test(trimmed) ? true : /^US\b/i.test(trimmed) || trimmed.includes("?") ? false : null;
  return {
    type: "reading",
    reading: {
      grams: overload ? null : toGrams(sign, digits.replace(",", "."), unit ?? "g"),
      unit: unit ?? "g",
      stable,
      overload,
      raw: line,
      at,
    },
  };
}

export const genericProtocol: BalanceProtocol = {
  id: "generic",
  label: "Generic number lines",
  parse: parseGenericLine,
  commands: { identify: [], tare: "T", zero: "Z" },
  lineEnding: "\r\n",
  serial: { baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1 },
};
